import { motion } from "framer-motion";
import { Store, Globe, Smartphone, Percent, ShieldCheck, ArrowUpRight } from "lucide-react";
import { IMAGES, scrollToId } from "@/lib/site";
import { Eyebrow, Reveal } from "@/components/Primitives";

const PhotoTile = ({ img, icon: Icon, tag, title, text, className, testid, tall }) => (
  <motion.article
    whileHover="hover"
    className={`group relative overflow-hidden rounded-[28px] bg-[var(--ink)] text-white ${className}`}
    data-testid={testid}
  >
    <motion.img
      src={img}
      alt={title}
      variants={{ hover: { scale: 1.06 } }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="absolute inset-0 h-full w-full object-cover opacity-80"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-[#0B1220]/55 to-transparent" />
    <div className={`relative h-full flex flex-col justify-end p-7 sm:p-9 ${tall ? "min-h-[460px]" : "min-h-[300px]"}`}>
      <div className="flex items-center justify-between mb-auto">
        <span className="grid place-items-center h-11 w-11 rounded-full bg-white/15 backdrop-blur-md">
          <Icon className="h-5 w-5" />
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-200">{tag}</span>
      </div>
      <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-10">{title}</h3>
      <p className="mt-3 text-sm sm:text-base text-slate-200 max-w-md leading-relaxed">{text}</p>
    </div>
  </motion.article>
);

const PlainTile = ({ icon: Icon, title, text, className, testid, dark }) => (
  <article
    className={`rounded-[28px] p-7 sm:p-9 flex flex-col justify-between min-h-[240px] border transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-xl ${
      dark ? "bg-[var(--cobalt)] text-white border-transparent" : "bg-white border-slate-200"
    } ${className}`}
    data-testid={testid}
  >
    <Icon className={`h-7 w-7 ${dark ? "text-white" : "text-[var(--cobalt)]"}`} />
    <div className="mt-10">
      <h3 className="font-display text-2xl font-semibold tracking-tight">{title}</h3>
      <p className={`mt-3 text-sm sm:text-base leading-relaxed ${dark ? "text-blue-50" : "text-slate-600"}`}>{text}</p>
    </div>
  </article>
);

export const Solutions = () => (
  <section id="solutions" className="py-24 lg:py-36" data-testid="solutions-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 lg:mb-20">
        <Reveal className="lg:col-span-7">
          <Eyebrow>01 — Solutions</Eyebrow>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.02]">
            One processor for <span className="font-serif-i text-[var(--cobalt)]">every</span> way you get paid.
          </h2>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.1}>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Whether you ring up at a counter, sell through a website or take payments in the field, we match you with
            the right equipment, gateway and pricing — and keep it all on one clear statement.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6">
        <Reveal className="md:col-span-7 md:row-span-2">
          <PhotoTile
            tall
            className="h-full"
            img={IMAGES.retail}
            icon={Store}
            tag="Retail"
            title="Countertop & smart POS systems"
            text="Modern terminals that take chip, tap and mobile wallets, with inventory, tipping and reporting built in."
            testid="solution-retail"
          />
        </Reveal>
        <Reveal className="md:col-span-5" delay={0.08}>
          <PhotoTile
            className="h-full"
            img={IMAGES.online}
            icon={Globe}
            tag="Online"
            title="E-commerce & payment gateways"
            text="Secure checkout, invoicing and recurring billing that plug into the store you already run."
            testid="solution-online"
          />
        </Reveal>
        <Reveal className="md:col-span-5" delay={0.16}>
          <PhotoTile
            className="h-full"
            img={IMAGES.mobile}
            icon={Smartphone}
            tag="Mobile"
            title="Mobile & on-the-go payments"
            text="Pocket readers and tap-to-pay on your phone for deliveries, markets, events and field work."
            testid="solution-mobile"
          />
        </Reveal>
        <Reveal className="md:col-span-6">
          <PlainTile
            dark
            className="h-full"
            icon={Percent}
            title="Cash discount & dual pricing"
            text="Ask about programs that can offset processing costs, set up compliantly where permitted."
            testid="solution-dual-pricing"
          />
        </Reveal>
        <Reveal className="md:col-span-6" delay={0.08}>
          <div className="h-full">
            <PlainTile
              className="h-full"
              icon={ShieldCheck}
              title="Security & real human support"
              text="PCI guidance, fraud tools and chargeback help from a team that answers when you call."
              testid="solution-security"
            />
          </div>
        </Reveal>
      </div>

      <Reveal className="mt-10 flex justify-end">
        <button
          onClick={() => scrollToId("quote")}
          data-testid="solutions-cta"
          className="group inline-flex items-center gap-2 font-semibold text-[var(--ink)] border-b-2 border-[var(--ink)] pb-1 hover:text-[var(--cobalt)] hover:border-[var(--cobalt)] transition-colors"
        >
          Find the right setup for my business
          <ArrowUpRight className="h-5 w-5 group-hover:rotate-45 transition-transform" />
        </button>
      </Reveal>
    </div>
  </section>
);
